import { useNavigate } from "react-router";

import { t } from "i18next";

import { Box, Button, Typography } from "@mui/material";

import { theme } from "@/src/theme/theme";

import { userAuctionsBadgesAtom } from "@/src/state/userAuctionsBadgesAtom";

import { PageContainer } from "@/src/components/atoms";

import { CONSUMER_HOMEPAGE_PREFIX } from "@/src/pages/consumer/HomePage/HomePageContent";
import { onAuctionListingClick } from "@/src/pages/consumer/HomePage/homePageUtils";
import DealerLogoCards from "@/src/pages/consumer/WeBuyCars/components/DealerLogoCards";

import { LoginSource } from "@/src/auth/auth";
import { useAuth } from "@/src/auth/hooks/useAuth";
import { VehicleType } from "@/src/types/types";
import type { DealerLogoData } from "@/src/utils/dealerLogoUtils";

import { useGTMDispatch } from "@elgorditosalsero/react-gtm-hook";
import { useAtom } from "jotai";

interface WeBuyCarsByMakerPageProps {
  carMaker?: string;
  dealerLogos: DealerLogoData;
}

const WeBuyCarsByMaker = ({
  carMaker,
  dealerLogos
}: WeBuyCarsByMakerPageProps) => {
  const { isAuthenticated, user, login } = useAuth();
  const sendDataToGTM = useGTMDispatch();
  const navigate = useNavigate();
  const [userAuctionsBadges] = useAtom(userAuctionsBadgesAtom);
  const draftAuction = userAuctionsBadges?.draftAuctionId;
  const activeAuction = userAuctionsBadges?.activeAuctionId;

  const pageTitle = t(
    carMaker
      ? "components.weBuyCarsByMakerPage.subtitleWithMaker"
      : "components.weBuyCarsByMakerPage.subtitle",
    { carMaker }
  );
  let buttonText = `${CONSUMER_HOMEPAGE_PREFIX}.startAuction`;

  if (draftAuction) {
    buttonText = `${CONSUMER_HOMEPAGE_PREFIX}.continueAuction`;
  }

  return (
    <PageContainer maxWidth="1200px" sx={{ margin: "auto", mt: 2 }}>
      <Typography
        variant="h1"
        sx={{
          pb: 3,
          fontWeight: theme.typography.fontWeightBold,
          textAlign: "center"
        }}
      >
        {t(
          carMaker
            ? "components.weBuyCarsByMakerPage.titleWithMaker"
            : "components.weBuyCarsByMakerPage.title",
          { carMaker }
        )}
      </Typography>
      <Typography variant="h2" sx={{ pb: 3, textAlign: "center" }}>
        {t("components.weBuyCarsByMakerPage.subtitle")}
      </Typography>

      <Typography variant="body1" sx={{ pb: 3, textAlign: "center" }}>
        {t(
          carMaker
            ? "components.weBuyCarsByMakerPage.body1WithMaker"
            : "components.weBuyCarsByMakerPage.body1",
          { carMaker }
        )}
      </Typography>
      <Box sx={{ justifyContent: "center", display: "flex", m: 2 }}>
        <Button
          sx={{ mb: 3 }}
          variant="contained"
          onClick={() =>
            onAuctionListingClick(
              LoginSource.WE_BUY_CARS_BY_MAKER_PAGE_CTA1,
              isAuthenticated,
              draftAuction,
              activeAuction,
              navigate,
              user,
              sendDataToGTM,
              login,
              VehicleType.CAR
            )
          }
        >
          {t(buttonText)}
        </Button>
      </Box>
      <Typography variant="h2" sx={{ pb: 3, textAlign: "center" }}>
        {t("components.weBuyCarsByMakerPage.title2")}
      </Typography>
      <Typography variant="body1" sx={{ pb: 1, textAlign: "center" }}>
        {t("components.weBuyCarsByMakerPage.body2")}
      </Typography>
      <Typography variant="body1" sx={{ pb: 3, textAlign: "center" }}>
        {t(
          carMaker
            ? "components.weBuyCarsByMakerPage.body3WithMaker"
            : "components.weBuyCarsByMakerPage.body3",
          { carMaker }
        )}
      </Typography>
      <Typography variant="h2" sx={{ pb: 3, textAlign: "center" }}>
        {t("components.weBuyCarsByMakerPage.title3")}
      </Typography>
      <Typography variant="body1" sx={{ textAlign: "center" }}>
        {t("components.weBuyCarsByMakerPage.body4")}
      </Typography>

      <Box sx={{ justifyContent: "center", display: "flex", m: 2 }}>
        <Button
          sx={{ mt: 2 }}
          variant="contained"
          onClick={() =>
            onAuctionListingClick(
              LoginSource.WE_BUY_CARS_BY_MAKER_PAGE_CTA2,
              isAuthenticated,
              draftAuction,
              activeAuction,
              navigate,
              user,
              sendDataToGTM,
              login,
              VehicleType.CAR
            )
          }
        >
          {t(buttonText)}
        </Button>
      </Box>
      <Typography variant="h2" sx={{ p: 2, textAlign: "center" }}>
        {pageTitle}
      </Typography>
      <DealerLogoCards dealerLogos={dealerLogos} />
    </PageContainer>
  );
};

export default WeBuyCarsByMaker;
